// SharePostDialog.tsx — "Share this post" sheet (1.23.90), opened from the
// Share… item in PostActions' share popover.
//
//   1. Preview  — a compact, non-interactive rendering of the post as it will
//                 look once shared: avatar, name (+ verified mark), @handle · time,
//                 body clamped to ~4 lines (rendered by the same safe PostBody the
//                 card uses), the first real image, and comment · cheer · like counts.
//   2. Share to — a tile grid: Copy link, the device's own share sheet (ONLY when
//                 navigator.share exists — no control wired to nothing), and one
//                 real anchor per platform (X / WhatsApp / Telegram / Facebook /
//                 Reddit / Email). Discord has no share URL, so it is not here.
//   3. Message  — an optional ≤280-char note that rides along in every platform
//                 href and in the native share payload. Copy link copies the URL only.
//   4. Footer   — ghost Done + primary Copy link.
//
// Sharing is NOT auth-gated: a guest may share a public post. The URL comes from
// core/lib/postUrl.ts (shareCode when present, legacy id otherwise) — the same
// helper PostMoreMenu's "Copy link to post" uses, so the two can never disagree.
//
// 1.24.00 E1 moved the parts this sheet shares with ShareProfileDialog out:
// the six platform hrefs + the note rules to core/lib/shareTargets.ts, the tile
// grid and the note field to components/share/ShareSheetParts.tsx. Nothing this
// file RENDERS changed — only where the pieces are declared.
//
// Every click inside the sheet (and on its backdrop) is stopped at ClickBoundary:
// the Dialog portals into <body>, but React still bubbles synthetic events up the
// REACT tree, and a feed PostCard's onCardClick would otherwise navigate away to
// /post/:id the moment someone tapped the preview, the textarea, or the backdrop.

import { useId, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import styled from 'styled-components';
import type { Post } from '@/core/models/post';
import { Dialog, Button, Avatar, TierBadge, deriveTier, useToast, MetaRow } from '@/ui';
import { Icon } from '@/icons';
import { buildPostShareUrl } from '@/core/lib/postUrl';
import { buildPlatformTargets } from '@/core/lib/shareTargets';
import {
  ShareMessageField,
  SharePlatformTile,
  ShareTileAction,
} from '@/components/share/ShareSheetParts';
import {
  ShareSectionLabel,
  ShareSrOnly,
  ShareTileGrid,
} from '@/components/share/shareChrome';
import { formatCount } from '@/components/formatCount';
import { PostBody } from './PostBody';
import { postImages } from './postImages';

// ─── Styled primitives ────────────────────────────────────────────────────────

const ClickBoundary = styled.div`
  display: contents;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// — Preview —

const Preview = styled.section`
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.surface};
`;

const PreviewHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PreviewMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const NameLine = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

const Name = styled.span`
  font-family: ${({ theme }) => theme.font.display};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HandleLine = styled.div`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// PostBody keeps the card's safe legacy-markup rendering (hashtag spans, <br>
// splitting, no innerHTML) AND its `white-space: pre-wrap`, so paragraph breaks
// survive in the preview — line-clamp works fine with pre-wrap. Only the display
// is overridden so the body can't run long.
const ClampedBody = styled(PostBody)`
  margin-top: 8px;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PreviewImg = styled.img`
  display: block;
  margin-top: 10px;
  width: 100%;
  max-height: 200px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.sm};
`;

const Counts = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Stat = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

// — Footer —

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

// ─── Component ────────────────────────────────────────────────────────────────

export interface SharePostDialogProps {
  post: Post;
  onClose: () => void;
  /** Share this address instead of the post's (the video watch page, maybe with ?t=). */
  url?: string;
  /** Extra controls above "Share to" (the watch page's "Start at 2:14"). */
  extra?: ReactNode;
}

export function SharePostDialog({ post, onClose, url: urlOverride, extra }: SharePostDialogProps) {
  const toast = useToast();
  const shareToLabelId = useId();
  const [message, setMessage] = useState('');

  const url = urlOverride ?? buildPostShareUrl(post);
  const title = `${post.name} (${post.handle}) on TruXSocial`;
  const image = postImages(post)[0];
  // Read once per render — a device either has the Web Share API or it doesn't.
  const canNativeShare = typeof navigator.share === 'function';
  const targets = buildPlatformTargets(url, title, message);

  function stop(e: ReactMouseEvent) {
    e.stopPropagation();
  }

  function handleCopyLink() {
    // navigator.clipboard is undefined outside a secure context (plain http, some
    // embedded webviews) — guard rather than let that throw synchronously.
    if (!navigator.clipboard) {
      toast('Could not copy link');
      return;
    }
    void navigator.clipboard.writeText(url).then(
      () => toast('Link copied'),
      () => toast('Could not copy link'),
    );
  }

  function handleNativeShare() {
    const data: ShareData = { title, url, ...(message ? { text: message } : {}) };
    void navigator.share(data).then(undefined, (err: unknown) => {
      // The user closing their own share sheet is not an error worth a toast.
      if ((err as { name?: unknown } | null)?.name === 'AbortError') return;
      toast('Could not share');
    });
  }

  return (
    <ClickBoundary onClick={stop}>
      <Dialog open title="Share this post" onClose={onClose}>
        <Stack>
          {/* 1. Preview — display only */}
          <Preview aria-label="Preview" data-testid="share-preview">
            <PreviewHead>
              <Avatar name={post.name} size={36} avatarKey={post.av} />
              <PreviewMeta>
                <NameLine>
                  <Name>{post.name}</Name>
                  <TierBadge tier={deriveTier(post.verified)} />
                </NameLine>
                <HandleLine>
                  <MetaRow>
                    <span>{post.handle}</span>
                    <span>{post.time}</span>
                  </MetaRow>
                </HandleLine>
              </PreviewMeta>
            </PreviewHead>
            {post.body && <ClampedBody body={post.body} inert />}
            {image && (
              <PreviewImg src={image.url} alt={image.alt} data-testid="share-preview-image" />
            )}
            <Counts>
              <Stat>
                <Icon name="comment" size={14} />
                {formatCount(post.comments)}
                <ShareSrOnly> comments</ShareSrOnly>
              </Stat>
              <Stat>
                <Icon name="cherish" size={14} />
                {formatCount(post.cheers)}
                <ShareSrOnly> cherishes</ShareSrOnly>
              </Stat>
              <Stat>
                <Icon name="spread-it" size={14} />
                {formatCount(post.spreads)}
                <ShareSrOnly> spreads</ShareSrOnly>
              </Stat>
            </Counts>
          </Preview>

          {extra}

          {/* 2. Share to */}
          <div>
            <ShareSectionLabel id={shareToLabelId}>Share to</ShareSectionLabel>
            <ShareTileGrid
              role="group"
              aria-labelledby={shareToLabelId}
              data-testid="share-targets"
            >
              <ShareTileAction icon="link" label="Copy link" onClick={handleCopyLink} />
              {canNativeShare && (
                <ShareTileAction icon="share" label="Share via…" onClick={handleNativeShare} />
              )}
              {targets.map((t) => (
                <SharePlatformTile key={t.key} target={t} />
              ))}
            </ShareTileGrid>
          </div>

          {/* 3. Optional message */}
          <ShareMessageField
            value={message}
            onChange={setMessage}
            placeholder="Say something about this post"
          />

          {/* 4. Footer */}
          <ButtonRow>
            <Button variant="ghost" onClick={onClose}>
              Done
            </Button>
            <Button variant="primary" onClick={handleCopyLink}>
              Copy link
            </Button>
          </ButtonRow>
        </Stack>
      </Dialog>
    </ClickBoundary>
  );
}
